// Lightweight Google Analytics helpers with Web Vitals tracking
// Safely no-op if gtag is unavailable (e.g., local dev or ad blockers)

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';

export type LinkClickParams = {
  link_url: string;
  link_text?: string;
  location?: string;
};

export const trackEvent = (action: string, params: Record<string, any> = {}) => {
  try {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', action, params);
    }
  } catch {
    // Silently ignore analytics errors
  }
};

export const trackLinkClick = (name: string, href: string, location?: string, text?: string) => {
  trackEvent('link_click', {
    event_category: 'engagement',
    event_label: name,
    link_url: href,
    location,
    link_text: text,
  });
};

/**
 * Track Web Vitals (Core Web Vitals + additional metrics)
 * Sends performance metrics to Google Analytics
 */
function sendToGoogleAnalytics(metric: Metric) {
  // Send to Google Analytics
  trackEvent(metric.name, {
    event_category: 'Web Vitals',
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    event_label: metric.id,
    non_interaction: true,
  });

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
    });
  }
}

/**
 * Initialize Web Vitals tracking
 * Call this once when the app starts
 */
export function initWebVitals() {
  try {
    // Core Web Vitals
    onCLS(sendToGoogleAnalytics); // Cumulative Layout Shift (< 0.1 is good)
    onINP(sendToGoogleAnalytics); // Interaction to Next Paint (< 200ms is good) - replaces FID
    onLCP(sendToGoogleAnalytics); // Largest Contentful Paint (< 2.5s is good)

    // Additional metrics
    onFCP(sendToGoogleAnalytics); // First Contentful Paint
    onTTFB(sendToGoogleAnalytics); // Time to First Byte

    console.log('✅ Web Vitals tracking initialized');
  } catch (error) {
    console.warn('Failed to initialize Web Vitals:', error);
  }
}

/**
 * Track custom performance marks
 */
export function trackPerformanceMark(name: string, startMark?: string) {
  try {
    if (!performance.mark) return;

    if (startMark && performance.measure) {
      performance.mark(name);
      const measure = performance.measure(`${startMark}-to-${name}`, startMark, name);

      trackEvent('performance_measure', {
        event_category: 'Performance',
        event_label: measure.name,
        value: Math.round(measure.duration),
      });
    } else {
      performance.mark(name);
    }
  } catch {
    // Silently ignore
  }
}

/**
 * Track page view with performance timing
 */
export function trackPageView(path: string) {
  trackEvent('page_view', {
    page_path: path,
    page_title: document.title,
  });

  // Track page load time if available
  if (performance.getEntriesByType) {
    const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationTiming) {
      const loadTime = navigationTiming.loadEventEnd - navigationTiming.fetchStart;
      trackEvent('page_load_time', {
        event_category: 'Performance',
        value: Math.round(loadTime),
        page_path: path,
      });
    }
  }
}
